import React, { useState, useEffect, useCallback } from "react";
import { useTheme } from "../../ThemeContext";
import { getApiDocsComplete } from "../../utils/apiClient";
import "./ApiDocs.css";

/**
 * Shows a loading overlay in the docs window using the project's loader GIF
 * External docs are always in light theme, so we use loader_black.gif
 * @param {Window} window - The docs window
 */
const showLoadingOverlay = (window) => {
  if (!window || window.closed || !window.document) return;
  
  try {
    // Remove existing overlay if any
    const existingOverlay = window.document.getElementById('api-docs-loading-overlay');
    if (existingOverlay) {
      existingOverlay.remove();
    }
    
    // Use light theme loader (loader_black.gif) since external docs are always light theme
    const loaderGif = '/icons/loader_black.gif';
    
    // Get the origin for absolute URL
    const origin = window.location.origin || (window.opener ? window.opener.location.origin : window.location.origin);
    const loaderUrl = origin + loaderGif;
    
    // Create overlay element
    const overlay = window.document.createElement('div');
    overlay.id = 'api-docs-loading-overlay';
    overlay.innerHTML = `
      <div style="
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(255, 255, 255, 0.95);
        z-index: 99999;
        display: flex;
        align-items: center;
        justify-content: center;
        backdrop-filter: blur(2px);
        transition: opacity 0.3s ease;
      ">
        <img 
          src="${loaderUrl}" 
          alt="Loading..." 
          class="loader"
          style="
            width: 150px;
            height: 150px;
          "
        />
      </div>
    `;
    
    window.document.body.appendChild(overlay);
  } catch (error) {
    console.error('Error showing loading overlay:', error);
  }
};

/**
 * Hides the loading overlay in the docs window
 */
const hideLoadingOverlay = (window) => {
  if (!window || window.closed || !window.document) return;
  
  try {
    const overlay = window.document.getElementById('api-docs-loading-overlay');
    if (overlay) {
      // Fade out animation
      overlay.style.opacity = '0';
      setTimeout(() => {
        if (overlay && overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
        }
      }, 300);
    }
  } catch (error) {
    console.error('Error hiding loading overlay:', error);
  }
};

const ApiDocs = () => {
  const { currentTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [docsWindow, setDocsWindow] = useState(null);
  const [isNavigating, setIsNavigating] = useState(false);

  // Function to open docs in a new tab
  const openDocsInNewTab = useCallback(async (page = 'index') => {
    try {
      setLoading(true);
      setError(null);

      // Fetch complete HTML with inlined assets
      const completeHtml = await getApiDocsComplete(page);

      // Open a new window/tab
      const newWindow = window.open('', '_blank');
      
      if (!newWindow) {
        throw new Error('Pop-up blocked. Please allow pop-ups for this site.');
      }

      // Write the complete HTML to the new window
      newWindow.document.open();
      newWindow.document.write(completeHtml);
      newWindow.document.close();

      // Store reference to the window
      setDocsWindow(newWindow);

      setLoading(false);
    } catch (err) {
      console.error("Failed to open API docs:", err);
      setError(err.message || "Failed to load API documentation. Please try again.");
      setLoading(false);
    }
  }, []);

  // Listen for navigation messages from the docs window
  useEffect(() => {
    const handleMessage = async (event) => {
      if (event.data && event.data.type === 'API_DOCS_NAVIGATE') {
        const page = event.data.page;
        
        // Prevent multiple simultaneous navigation requests
        if (isNavigating) {
          console.log('⏸️ Navigation already in progress, skipping...');
          return;
        }
        
        console.log('📄 Navigating to docs page:', page);
        
        // Show loading state immediately
        setIsNavigating(true);
        setLoading(true);
        
        try {
          // Fetch the new page (assets are cached, so this should be fast)
          const completeHtml = await getApiDocsComplete(page, false);
          console.log('✅ Got HTML for page:', page, 'length:', completeHtml.length);
          
          // Write to the existing docs window
          if (docsWindow && !docsWindow.closed) {
            try {
              // Show loading overlay in the docs window
              showLoadingOverlay(docsWindow);
              
              // Small delay to ensure overlay is visible before heavy processing
              await new Promise(resolve => setTimeout(resolve, 50));
              
              docsWindow.document.open();
              docsWindow.document.write(completeHtml);
              docsWindow.document.close();
              
              // Hide loading overlay after content is loaded
              setTimeout(() => {
                hideLoadingOverlay(docsWindow);
              }, 100);
              
              setLoading(false);
              setIsNavigating(false);
            } catch (writeError) {
              console.error('❌ Error writing to window:', writeError);
              hideLoadingOverlay(docsWindow);
              setLoading(false);
              setIsNavigating(false);
              // If writing fails, try opening a new window
              openDocsInNewTab(page);
            }
          } else {
            // Window was closed, open a new one
            setLoading(false);
            setIsNavigating(false);
            openDocsInNewTab(page);
          }
        } catch (err) {
          console.error('❌ Failed to navigate to docs page:', err);
          setLoading(false);
          setIsNavigating(false);
          setError(err.message || 'Failed to load page. Please try again.');
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [docsWindow, openDocsInNewTab, isNavigating]);

  // Auto-open docs when component mounts
  useEffect(() => {
    openDocsInNewTab('index');
  }, [openDocsInNewTab]);

  return (
    <div
      className="api-docs-container"
      style={{
        background: currentTheme.homePage.mainBackground,
        color: currentTheme.homePage.textColor,
      }}
    >
      <div className="api-docs-message">
        {loading ? (
          <div className="api-docs-loading">
            <div className="api-docs-spinner"></div>
            <p>Loading API Documentation...</p>
          </div>
        ) : error ? (
          <div className="api-docs-error">
            <div className="api-docs-error-icon">⚠️</div>
            <p>{error}</p>
            <button
              onClick={() => openDocsInNewTab('index')}
              className="api-docs-retry-btn"
            >
              Try Again
            </button>
          </div>
        ) : (
          <div className="api-docs-success">
            <div className="api-docs-icon">📚</div>
            <h2>API Documentation</h2>
            <p>The API documentation has been opened in a new browser tab.</p>
            <p className="api-docs-hint">
              If the documentation didn't open, please check if pop-ups are blocked.
            </p>
            <button
              onClick={() => openDocsInNewTab('index')}
              className="api-docs-open-btn"
            >
              Open Documentation Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiDocs;
